import {Component, Inject, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {InspectionService} from '../../services/inspection.service';
import {DraftInspection, InspectionModel} from '../../../../models/inspection.model';
import {EquipmentPointModel} from '../../../../models/equipment-point.model';
import {InspectionDraftService} from "../../services/inspection-draft.service";

export interface EditInspectionDialogData {
    point: EquipmentPointModel;
    isEditMode?: boolean;
}

// Validateur personnalisé pour s'assurer qu'il y a au moins 10 valeurs non nulles
export function minNonNullValuesValidator(minCount: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const formArray = control as FormArray;
        const nonNullCount = formArray.controls
            .map(ctrl => ctrl.value)
            .filter((value: any) => value !== null && value !== undefined && value !== '')
            .length;

        return nonNullCount >= minCount ? null : {'minNonNullValues': {required: minCount, actual: nonNullCount}};
    };
}

@Component({
    selector: 'app-edit-inspection-dialog',
    templateUrl: './edit-inspection-dialog.component.html',
    styleUrls: ['./edit-inspection-dialog.component.scss']
})
export class EditInspectionDialogComponent implements OnInit {
    form: FormGroup;

    previousInspectionsList: InspectionModel[] = [];

    isEditMode: boolean = false;
    draftInspection: DraftInspection | null = null;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<EditInspectionDialogComponent>,
        private inspectionService: InspectionService,
        @Inject(MAT_DIALOG_DATA) public data: EditInspectionDialogData,
        private inspectionDraftService: InspectionDraftService
    ) {
        this.draftInspection = this.inspectionDraftService.getDraft();

        // Initialiser le formulaire avec les validateurs appropriés
        // Créer un validateur personnalisé pour circPercent (doit être > 0 et <= 1)
        const circPercentValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (value === null || value === undefined || value === '') {
                return null; // Laissez Validators.required gérer ce cas
            }

            const numValue = Number(value);
            if (isNaN(numValue)) {
                return {'notANumber': true};
            }

            if (numValue <= 0) {
                return {'positiveRequired': true};
            }

            if (numValue > 1) {
                return {'maxValue': {max: 1, actual: numValue}};
            }

            return null;
        };

        // Validateur pour les nombres positifs
        const positiveNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const numValue = Number(value);
            if (isNaN(numValue)) {
                return {'notANumber': true};
            }

            if (numValue <= 0) {
                return {'positiveRequired': true};
            }

            return null;
        };

        this.form = this.fb.group({
            circPercent: [
                this.draftInspection?.circPercent,
                [
                    Validators.required,
                    circPercentValidator
                ]
            ],
            length: [
                this.draftInspection?.length,
                [
                    Validators.required,
                    positiveNumberValidator
                ]
            ],
            measurements: this.fb.array([], minNonNullValuesValidator(20))
        });

        this.isEditMode = !!data.isEditMode;
    }

    get measurementsArray(): FormArray {
        return this.form.get('measurements') as FormArray;
    }

    // Getters pour faciliter les validations dans le template
    get circPercentControl() {
        return this.form.get('circPercent');
    }

    get lengthControl() {
        return this.form.get('length');
    }

    ngOnInit(): void {
        const pointId = this.data.point.id;
        this.loadLastInspections(pointId, this.draftInspection!.inspectionDate!.toString());
        const draft = this.inspectionDraftService.getDraft();

        if (this.isEditMode || (draft.measurements && draft.measurements.length > 0)) {
            if (draft && draft.measurements) {
                draft.measurements.forEach(m => {
                    // Ne pas ajouter ici les validateurs, ils seront ajoutés dans InspectionTableComponent
                    this.measurementsArray.push(this.fb.control(m.value));
                });
            }
        } else {
            Array.from({length: 20}, (_, i) => i).forEach(m => {
                // Ne pas ajouter ici les validateurs, ils seront ajoutés dans InspectionTableComponent
                this.measurementsArray.push(this.fb.control(null));
            });
        }
    }

    /**
     * Méthode appelée lors du clic sur "Paste from Excel"
     * Lit le presse-papier et insère les valeurs dans le FormArray
     */
    onPasteFromExcel(): void {
        navigator.clipboard.readText().then(text => {
            const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l);

            const numericRegex = /^[+-]?\d+([.,]\d+)?$/;

            const validValues: number[] = [];
            for (const line of lines) {
                if (numericRegex.test(line)) {
                    // Remplacer la virgule par un point avant parseFloat
                    const numericValue = parseFloat(line.replace(',', '.'));
                    if (!isNaN(numericValue) && numericValue > 0) { // N'accepter que les valeurs positives
                        validValues.push(numericValue);
                    }
                } else {
                    // Ligne ignorée car elle contient des caractères non numériques
                    console.warn('Ignoring invalid line:', line);
                }
            }

            // Créer un validateur pour chaque valeur individuelle
            const positiveNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
                const value = control.value;
                if (value === null || value === undefined || value === '') {
                    return null;
                }

                const numValue = Number(value);
                if (isNaN(numValue)) {
                    return {'notANumber': true};
                }

                if (numValue <= 0) {
                    return {'positiveRequired': true};
                }

                return null;
            };

            const newArray = this.fb.array(
                validValues.map(val => this.fb.control(val, [positiveNumberValidator])),
                minNonNullValuesValidator(10)
            );

            this.form.setControl('measurements', newArray);
        }).catch(err => {
            console.error('Impossible de lire le presse-papier', err);
        });
    }

    isFormInvalid(): boolean {
        // Vérifier si le formulaire est invalide ou si le nombre de mesures est insuffisant
        // Vérifier également si l'un des contrôles de mesure est invalide
        const hasMeasurementError = this.measurementsArray.controls.some((control: any) => control.invalid);
        return this.form.invalid || this.measurementsArray.value.filter((val: any) => val !== null).length < 10 || hasMeasurementError;
    }

    onSave(): void {
        if (this.isFormInvalid()) {
            return;
        }
        // Check threshold
        const measures: number[] = this.measurementsArray.value;
        const anyAbove = this.checkThreshold(measures);

        this.updateDraft(measures);

        if (anyAbove) {
            this.dialogRef.close('THRESHOLD');
        } else {
            this.dialogRef.close('OK');
        }
    }

    updateDraft(measures: number[]): void {
        this.draftInspection = {
            ...this.draftInspection,
            circPercent: this.form.value.circPercent,
            length: this.form.value.length,
            measurements: measures
                .filter(val => val != null)
                .map(val => ({value: val}))
        }
        this.inspectionDraftService.updateDraft(this.draftInspection);
    }

    onCancel(): void {
        this.dialogRef.close('CANCEL');
    }

    private checkThreshold(measures: number[]): boolean {
        const threshold = this.data.point.initialUncoorTHK;
        if (!threshold) return false;
        return measures.some(val => val != null && val > threshold);
    }

    private loadLastInspections(pointId: number, inspectionDate: string) {
        this.inspectionService.getLastInspections(pointId, inspectionDate).subscribe({
            next: (allInspections) => {
                allInspections.data.sort((a, b) =>
                    new Date(a.inspectionDate).getTime() - new Date(b.inspectionDate).getTime()
                );
                this.previousInspectionsList = allInspections.data;
            },
            error: (err) => {
                console.error('Error loading inspections', err);
            }
        });
    }

    // Méthodes d'aide pour le template
    hasError(controlName: string, errorName: string): boolean {
        const control = this.form.get(controlName);
        return !!control && control.hasError(errorName) && (control.dirty || control.touched);
    }

    getMeasurementsValidationMessage(): string {
        // Vérifier d'abord s'il y a l'erreur de nombre minimum de mesures
        if (this.measurementsArray.hasError('minNonNullValues')) {
            return 'pages.editInspectionDialog.errors.minMeasurements';
        }

        // Vérifier si des valeurs négatives sont présentes
        const hasNegativeValues = this.measurementsArray.controls.some(
            control => control.value !== null &&
                control.value !== undefined &&
                control.value !== '' &&
                Number(control.value) <= 0
        );

        if (hasNegativeValues) {
            return 'pages.editInspectionDialog.errors.negativeValuesFound';
        }

        // Vérifier d'autres erreurs potentielles dans les contrôles individuels
        const hasOtherErrors = this.measurementsArray.controls.some(
            control => control.invalid
        );

        if (hasOtherErrors) {
            return 'pages.editInspectionDialog.errors.invalidMeasurements';
        }

        return '';
    }

    // Méthode pour compter les valeurs valides sans utiliser d'expressions complexes dans le template
    getValidMeasurementsCount(): number {
        return this.measurementsArray.controls
            .filter(control => control.value !== null && control.value !== undefined && control.value !== '')
            .length;
    }
}
